import React, { useMemo } from 'react';
import { Linking, StyleProp, StyleSheet, TextStyle, View, ViewStyle } from 'react-native';

import { Text } from '@fhs/ui';

export interface MarkdownStyles {
  heading?: StyleProp<TextStyle>;
  heading1?: StyleProp<TextStyle>;
  heading2?: StyleProp<TextStyle>;
  heading3?: StyleProp<TextStyle>;
  heading4?: StyleProp<TextStyle>;
  heading5?: StyleProp<TextStyle>;
  heading6?: StyleProp<TextStyle>;
  paragraph?: StyleProp<TextStyle>;
  bold?: StyleProp<TextStyle>;
  italic?: StyleProp<TextStyle>;
  link?: StyleProp<TextStyle>;
  text?: StyleProp<TextStyle>;
}

export interface MarkdownProps {
  children: string;
  style?: StyleProp<ViewStyle>;
  styles?: MarkdownStyles;
}

// Only supporting headings and paragraphs for now
interface MarkdownToken {
  type: 'heading' | 'paragraph';
  content: string;
  level?: number;
}

interface InlineToken {
  type: 'text' | 'bold' | 'italic' | 'link';
  content: string;
  url?: string;
}

export function Markdown({ children, style, styles }: MarkdownProps) {
  const tokens = useMemo(() => parseMarkdown(children), [children]);
  const parsedTokens = useMemo(() => {
    return tokens.map((token, index) => renderToken(token, index, styles));
  }, [tokens, styles]);

  return <View style={style}>{parsedTokens}</View>;
}

function parseMarkdown(markdown: string): MarkdownToken[] {
  const lines = markdown.split('\n');
  const tokens: MarkdownToken[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trim();

    if (!line) {
      i++;
      continue;
    }

    // Headers
    if (line.startsWith('#')) {
      const match = line.match(/^(#{1,6})\s+(.+)/);
      if (match) {
        tokens.push({
          type: 'heading',
          level: match[1].length,
          content: match[2],
        });
        i++;
        continue;
      }
    }

    // Regular paragraph
    const paragraphLines: string[] = [];
    while (i < lines.length) {
      const currentLine = lines[i].trim();
      if (
        currentLine === '' ||
        currentLine.startsWith('#') ||
        currentLine.startsWith('>') ||
        currentLine.startsWith('```') ||
        currentLine.match(/^(\*|\-|\+|\d+\.)\s+/) ||
        currentLine.match(/^(-{3,}|\*{3,}|_{3,})$/)
      ) {
        break;
      }
      paragraphLines.push(currentLine);
      i++;
    }

    if (paragraphLines.length > 0) {
      tokens.push({
        type: 'paragraph',
        content: paragraphLines.join(' '),
      });
    }
  }

  return tokens;
}

function parseInlineMarkdown(text: string): InlineToken[] {
  const tokens: InlineToken[] = [];
  let current = '';
  let i = 0;

  while (i < text.length) {
    const char = text[i];
    const nextChar = text[i + 1];

    // Bold (**text**)
    if (char === '*' && nextChar === '*') {
      if (current) {
        tokens.push({ type: 'text', content: current });
        current = '';
      }

      i += 2; // Skip **
      let boldContent = '';
      while (i < text.length - 1 && !(text[i] === '*' && text[i + 1] === '*')) {
        boldContent += text[i];
        i++;
      }

      if (i < text.length - 1) {
        tokens.push({ type: 'bold', content: boldContent });
        i += 2; // Skip closing **
      } else {
        current += '**' + boldContent;
        i++;
      }
      continue;
    }

    // Italic (*text*)
    if (char === '*' && nextChar !== '*') {
      if (current) {
        tokens.push({ type: 'text', content: current });
        current = '';
      }

      i++; // Skip *
      let italicContent = '';
      while (i < text.length && text[i] !== '*') {
        italicContent += text[i];
        i++;
      }

      if (i < text.length) {
        tokens.push({ type: 'italic', content: italicContent });
        i++; // Skip closing *
      } else {
        current += '*' + italicContent;
      }
      continue;
    }

    // Italic (_text_)
    if (char === '_') {
      if (current) {
        tokens.push({ type: 'text', content: current });
        current = '';
      }

      i++; // Skip _
      let italicContent = '';
      while (i < text.length && text[i] !== '_') {
        italicContent += text[i];
        i++;
      }

      if (i < text.length) {
        tokens.push({ type: 'italic', content: italicContent });
        i++; // Skip closing _
      } else {
        current += '_' + italicContent;
      }
      continue;
    }

    // Links [text](url)
    if (char === '[') {
      const linkMatch = text.substring(i).match(/^\[([^\]]+)\]\(([^)]+)\)/);
      if (linkMatch) {
        if (current) {
          tokens.push({ type: 'text', content: current });
          current = '';
        }

        tokens.push({
          type: 'link',
          content: linkMatch[1],
          url: linkMatch[2],
        });

        i += linkMatch[0].length;
        continue;
      }
    }

    current += char;
    i++;
  }

  if (current) {
    tokens.push({ type: 'text', content: current });
  }

  return tokens;
}

function renderToken(
  token: MarkdownToken,
  index: number,
  customStyles?: MarkdownStyles
): React.ReactElement {
  switch (token.type) {
    case 'heading':
      return renderHeading(token, index, customStyles);
    case 'paragraph':
      return renderParagraph(token, index, customStyles);
    default:
      return <View key={index} />;
  }
}

function renderHeading(
  token: MarkdownToken,
  index: number,
  customStyles?: MarkdownStyles
): React.ReactElement {
  const headingTypes: { [key: number]: 'hero' | 'one' | 'two' | 'three' | 'four' | 'five' } = {
    1: 'one',
    2: 'two',
    3: 'three',
    4: 'four',
    5: 'five',
    6: 'five',
  };

  const type = headingTypes[token.level || 1] || 'three';
  const inlineTokens = parseInlineMarkdown(token.content);

  // Get custom style for specific heading level or general heading style
  const headingLevelStyle = customStyles?.[`heading${token.level}` as keyof MarkdownStyles];
  const generalHeadingStyle = customStyles?.heading;
  const combinedHeadingStyle = [generalHeadingStyle, headingLevelStyle];

  return (
    <Text.Heading key={index} type={type} style={combinedHeadingStyle}>
      {inlineTokens.map((inlineToken, inlineIndex) =>
        renderInlineToken(inlineToken, `${index}-${inlineIndex}`, customStyles)
      )}
    </Text.Heading>
  );
}

function renderParagraph(
  token: MarkdownToken,
  index: number,
  customStyles?: MarkdownStyles
): React.ReactElement {
  const inlineTokens = parseInlineMarkdown(token.content);

  return (
    <Text.Paragraph key={index} size="md" style={customStyles?.paragraph}>
      {inlineTokens.map((inlineToken, inlineIndex) =>
        renderInlineToken(inlineToken, `${index}-${inlineIndex}`, customStyles)
      )}
    </Text.Paragraph>
  );
}

function renderInlineToken(
  token: InlineToken,
  key: string,
  customStyles?: MarkdownStyles
): React.ReactElement {
  switch (token.type) {
    case 'bold':
      return (
        <Text key={key} weight="semibold" style={customStyles?.bold}>
          {token.content}
        </Text>
      );
    case 'italic':
      return (
        <Text key={key} style={[styles.italic, customStyles?.italic]}>
          {token.content}
        </Text>
      );
    case 'link':
      return (
        <Text
          key={key}
          style={[styles.link, customStyles?.link]}
          onPress={() => handleLinkPress(token.url || '')}
        >
          {token.content}
        </Text>
      );
    case 'text':
    default:
      return (
        <Text key={key} style={customStyles?.text}>
          {token.content}
        </Text>
      );
  }
}

function handleLinkPress(url: string) {
  if (url) {
    Linking.canOpenURL(url).then(supported => {
      if (supported) {
        Linking.openURL(url);
      }
    });
  }
}

const styles = StyleSheet.create({
  italic: {
    fontStyle: 'italic',
  },
  link: {
    color: '#007AFF',
    textDecorationLine: 'underline',
  },
});
